import type { CollectionClientEventData, MemoryClientEventData } from '~/@types/backend-types'

import {
    ClientEventTypeDTO,
    getPostRequestClientEventMutationOptions,
    getPostRequestNameMutationOptions,
    usePostRequestClientEvent,
    usePostRequestName,
} from '@capture/client-api/src/orval'

import { buildCommonParams, buildRequestOptions } from './api'
import { mutationWithInvalidation } from './mutationWithInvalidation'

// ============================================================================
// Client Event DTO Converters
// ============================================================================

function collectionEventData2ClientEventRequestDTO(data: CollectionClientEventData) {
    return {
        events: [
            {
                event_type: ClientEventTypeDTO.viewed_collection_image,
                timestamp: (Date.now() / 1000).toString(),
                data,
            },
        ],
    }
}

export function getMemoryYearFromDateRange(dateRange: string) {
    return parseInt(dateRange.split('--')[0].split('-')[0], 10)
}

function memoryEventData2ClientEventRequestDTO(data: MemoryClientEventData) {
    return {
        events: [
            {
                event_type: ClientEventTypeDTO.viewed_memory_image,
                timestamp: (Date.now() / 1000).toString(),
                data: {
                    uuid: data.uuid,
                    date_range: data.date_range,
                    memory_year: getMemoryYearFromDateRange(data.date_range),
                },
            },
        ],
    }
}

// ============================================================================
// Collection Image Viewed Mutations
// ============================================================================

/**
 * Imperative mutation for tracking viewed collection image.
 * Use this in non-React contexts (e.g., Redux thunks, route loaders).
 *
 * @see ./mutationInvalidationMap.ts for the centralized invalidation rules
 */
export async function mutateViewedCollectionImage(data: CollectionClientEventData) {
    const commonParams = buildCommonParams()

    return mutationWithInvalidation(
        getPostRequestClientEventMutationOptions({ request: buildRequestOptions() }),
        {
            data: collectionEventData2ClientEventRequestDTO(data),
            params: commonParams,
        },
    )
}

/**
 * React hook for tracking viewed collection image mutation.
 *
 * Cache invalidation is handled automatically by the centralized MutationCache
 * configured in ./index.ts.
 *
 * @see ./mutationInvalidationMap.ts for the invalidation rules
 */
export function useViewedCollectionImageMutation() {
    const commonParams = buildCommonParams()

    const mutation = usePostRequestClientEvent({
        request: buildRequestOptions(),
    })

    return {
        ...mutation,
        mutate: (data: CollectionClientEventData) => {
            mutation.mutate({
                data: collectionEventData2ClientEventRequestDTO(data),
                params: commonParams,
            })
        },
    }
}

// ============================================================================
// Memory Image Viewed Mutations
// ============================================================================

/**
 * Imperative mutation for tracking viewed memory image.
 * Use this in non-React contexts (e.g., Redux thunks, route loaders).
 *
 * @see ./mutationInvalidationMap.ts for the centralized invalidation rules
 */
export async function mutateViewedMemoryImage(data: MemoryClientEventData) {
    const commonParams = buildCommonParams()

    return mutationWithInvalidation(
        getPostRequestClientEventMutationOptions({ request: buildRequestOptions() }),
        {
            data: memoryEventData2ClientEventRequestDTO(data),
            params: commonParams,
        },
    )
}

/**
 * React hook for tracking viewed memory image mutation.
 *
 * Cache invalidation is handled automatically by the centralized MutationCache
 * configured in ./index.ts.
 *
 * @see ./mutationInvalidationMap.ts for the invalidation rules
 */
export function useViewedMemoryImageMutation() {
    const commonParams = buildCommonParams()

    const mutation = usePostRequestClientEvent({
        request: buildRequestOptions(),
    })

    return {
        ...mutation,
        mutate: (data: MemoryClientEventData) => {
            mutation.mutate({
                data: memoryEventData2ClientEventRequestDTO(data),
                params: commonParams,
            })
        },
    }
}

// ============================================================================
// Account Info Mutations
// ============================================================================

/**
 * Imperative mutation for setting profile name.
 * Use this in non-React contexts (e.g., Redux thunks, route loaders).
 *
 * @see ./mutationInvalidationMap.ts for the centralized invalidation rules
 */
export async function mutateSetProfileName(name: string) {
    return mutationWithInvalidation(
        getPostRequestNameMutationOptions({ request: buildRequestOptions() }),
        { params: { ...buildCommonParams(), name } },
    )
}

/**
 * React hook for setting profile name mutation.
 *
 * Cache invalidation is handled automatically by the centralized MutationCache
 * configured in ./index.ts.
 *
 * @see ./mutationInvalidationMap.ts for the invalidation rules
 *
 * @example
 * const { mutate, isPending } = useSetProfileNameMutation()
 * mutate('New Name')
 */
export function useSetProfileNameMutation() {
    const commonParams = buildCommonParams()

    const mutation = usePostRequestName({
        request: buildRequestOptions(),
        // No onSuccess needed - MutationCache handles invalidation automatically
    })

    return {
        ...mutation,
        mutate: (name: string) => {
            mutation.mutate({ params: { ...commonParams, name } })
        },
        mutateAsync: async (name: string) => {
            return mutation.mutateAsync({ params: { ...commonParams, name } })
        },
    }
}
